import React, { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Redirect,
    Route,
    RouteComponentProps,
    Switch,
    withRouter,
} from 'react-router-dom';
import {
    createTheme,
    ThemeProvider as MuiThemeProvider,
} from '@material-ui/core/styles';
import Helmet from 'react-helmet';
import styled from 'styled-components';
import Modal from './components/Modal/Modal';
import ModalConductor from './components/Modal/ModalConductor';
import BookingProcess from './pages/BookingProcess';
import CancelBooking from './pages/CancelBooking';
import Checkout from './pages/Checkout';
import Complete from './pages/Checkout/Complete';
import Confirmation from './pages/Checkout/Confirmation';
import EmailSignup from './pages/EmailSignup';
import ThankYou from './pages/EmailSignup/ThankYou';
import { useRootStoreContext } from './stores/RootStore';
import theme from './styles/theme';

import { trackPageView } from '@dineouticeland/do-tracking';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { defaultQueryFn } from './defaultQueryFn';
import { DownloadApp } from './pages/DownloadApp';
import { EventRedirect } from './pages/Events/EventRedirect';
import { ErrorBoundary } from 'react-error-boundary';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const Home = React.lazy(() => import('./pages/Home'));
const Nearby = React.lazy(() => import('./pages/Nearby'));
const PaymentLink = React.lazy(() => import('./pages/PaymentLink'));
const Restaurant = React.lazy(() => import('./pages/Restaurant'));
const SatisfactionSurvey = React.lazy(
    () => import('./pages/SatisfactionSurvey'),
);
const Styleguide = React.lazy(() => import('./pages/Styleguide'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Unsubscribe = React.lazy(() => import('./pages/Unsubscribe'));
const TALayout = React.lazy(() => import('./pages/TA'));
const Restaurants = React.lazy(() => import('./pages/Restaurants/Restaurants'));
const OrderFood = React.lazy(() => import('./pages/Restaurants/OrderFood'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const Logout = React.lazy(() => import('./pages/Logout'));
const Xmas = React.lazy(() => import('./pages/Events/Xmas'));
const GuestList = React.lazy(() => import('./pages/GuestList'));
const GiftCardStatus = React.lazy(
    () => import('./pages/GiftCard/GiftCardStatus'),
);
const GiftCardView = React.lazy(() => import('./pages/GiftCard/GiftCardView'));

const TestStyle = styled.div`
    position: fixed;
    top: 46px;
    right: -40px;
    transform: rotate(45deg);

    background-color: red;
    padding: 4px 80px;
    color: #fff;
    font-size: 16px;

    z-index: 999999999999;
    pointer-events: none;
`;

type Props = RouteComponentProps<any>;

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: false,
            queryFn: defaultQueryFn,
        },
    },
});

const Layout: FC<Props> = ({ history }) => {
    const {
        UIStore: {
            reducer: [uiStoreState],
        },
    } = useRootStoreContext();

    const { i18n } = useTranslation();

    const primaryColor =
        uiStoreState.theme?.primary || theme.palette.primary.main;

    const muiTheme = useMemo(
        () =>
            createTheme({
                palette: {
                    type: 'light',
                    primary: {
                        main:
                            uiStoreState.theme?.primary ||
                            theme.palette.primary.main,
                        contrastText:
                            uiStoreState.theme?.onPrimary ||
                            theme.palette.primary.contrastText,
                    },
                    secondary: {
                        main:
                            uiStoreState.theme?.secondary ||
                            theme.palette.secondary.main,
                        contrastText:
                            uiStoreState.theme?.onSecondary ||
                            theme.palette.secondary.contrastText,
                    },
                    background: {
                        paper:
                            uiStoreState.theme?.surface ||
                            theme.palette.surface.main,
                        default:
                            uiStoreState.theme?.background ||
                            theme.palette.background.main,
                    },
                    error: {
                        main:
                            uiStoreState.theme?.error ||
                            theme.palette.error.main,
                        contrastText:
                            uiStoreState.theme?.onError ||
                            theme.palette.error.contrastText,
                    },
                    success: {
                        main: theme.colors.success,
                    },
                },
                typography: {
                    fontFamily: 'DM Sans',
                },
                overrides: {
                    MuiInputBase: {
                        root: {
                            '&.MuiInput-underline::before, &.MuiFilledInput-root::before':
                            {
                                display: 'none',
                            },
                            '&.MuiInput-underline::after, &.MuiFilledInput-root::after':
                            {
                                display: 'none',
                            },
                            '&.MuiFilledInput-root': {
                                backgroundColor: '#f7f8fa',
                            },
                            borderRadius: 4,
                        },
                    },
                    MuiInputLabel: {
                        root: {
                            textTransform: 'capitalize',
                        },
                    },
                    MuiFormControlLabel: {
                        root: {
                            textTransform: 'initial',
                            color: '#333',
                        },
                    },
                    MuiButton: {
                        root: {
                            outline: 'none !important',
                        },
                        contained: {
                            boxShadow: 'none',
                            backgroundColor: primaryColor,
                            color: '#fff !important',
                            padding: '16px 16px',
                            outline: 'none !important',
                            '&:hover': {
                                backgroundColor: primaryColor,
                            },
                            '&:active': {
                                backgroundColor: primaryColor,
                            },
                            '&.Mui-disabled': {
                                backgroundColor: primaryColor,
                                opacity: 0.5,
                            },
                        },
                    },
                    MuiTypography: {
                        h1: {
                            fontSize: 56,
                            fontWeight: 600,
                        },
                    },
                },
            }),
        [primaryColor, uiStoreState.theme],
    );

    /*
     * Scroll to top when pages change
     */
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [history.location.pathname]);

    /*
     * The single SPA route-change page view (do-tracking v2). GA4/Meta
     * automatic page views are disabled, so this is the one hook that fires a
     * platform page view on navigation. Keyed on pathname (not search) so the
     * language-param `history.replace` below doesn't double-fire.
     */
    useEffect(() => {
        trackPageView(window.location.href, document.title);
    }, [history.location.pathname]);

    /*
     * Append language parameter to url on change
     */
    useEffect(() => {
        const query = new URLSearchParams(history.location.search);
        query.set('lng', i18n.language);
        // Pathnames that end on / make our site behave weirdly
        const safePathname =
            history.location.pathname.endsWith('/') &&
                history.location.pathname.length > 1
                ? history.location.pathname.slice(0, -1)
                : history.location.pathname;

        history.replace({
            ...history.location,
            search: query.toString(),
            pathname: safePathname,
        });
    }, [history, history.location.search, i18n.language]);

    const metaTags = useMemo(() => {
        const { pageTitle, shareTitle, description, imageUrl, siteUrl } =
            uiStoreState.metaTagsConfig;

        return (
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="Description" content={description} />

                {/* Search Engine */}
                <meta name="description" content={description} />
                <meta name="image" content={imageUrl} />
                {/* Schema.org for Google */}
                <meta itemProp="name" content={shareTitle} />
                <meta itemProp="description" content={description} />
                <meta itemProp="image" content={imageUrl} />
                {/* Twitter */}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={shareTitle} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:site" content={siteUrl} />
                <meta name="twitter:creator" content="Dineout Iceland" />
                <meta name="twitter:image:src" content={imageUrl} />
                {/* Open Graph general (Facebook, Pinterest & Google+) */}
                <meta name="og:title" content={shareTitle} />
                <meta name="og:description" content={description} />
                <meta name="og:image" content={imageUrl} />
                <meta name="og:url" content={siteUrl} />
                <meta name="og:site_name" content={shareTitle} />
            </Helmet>
        );
    }, [uiStoreState.metaTagsConfig]);

    return (
        <QueryClientProvider client={queryClient}>
            <ErrorBoundary fallback={<p>Something went wrong</p>}>
                <MuiThemeProvider theme={muiTheme}>
                    {import.meta.env.VITE_NODE_ENV === 'staging' && (
                        <TestStyle>TEST</TestStyle>
                    )}

                    {metaTags}
                    <Modal>
                        <ModalConductor />
                    </Modal>

                    <Switch>
                        <Route path="/konukvold" exact>
                            <Redirect to="/lyng/konukvold" />
                        </Route>
                        {/*hotfix because of a slug change after qr code creation*/}
                        <Route
                            path="/bullseye/order/onsite:rest(.*)"
                            render={({ match, location }) => {
                                window.location.replace(
                                    `/bullseyereykjavik/order/onsite${match.params.rest}${location.search}`,
                                );
                                return null;
                            }}
                        />
                        <Route path="/" exact component={Home} />
                        <Route
                            path="/download-app"
                            exact
                            component={DownloadApp}
                        />
                        <Route
                            path="/gjafabref"
                            component={() => {
                                window.location.href =
                                    'https://giftcards.dineout.is/';
                                return null;
                            }}
                        />

                        <Route path="/results" exact component={Restaurants} />
                        <Route
                            path="/bookatable"
                            exact
                            component={Restaurants}
                        />
                        <Route path="/bokabord" exact component={Restaurants} />

                        <Route path="/delivery" exact component={OrderFood} />
                        <Route path="/takeaway" exact component={OrderFood} />
                        <Route path="/pantamat" exact component={OrderFood} />
                        <Route path="/order" exact component={OrderFood} />
                        <Route path="/orderfood" exact component={OrderFood} />
                        <Route path="/parties" exact>
                            <Redirect to="/pantamat?party=true" />
                        </Route>
                        <Route path="/party" exact>
                            <Redirect to="/pantamat?party=true" />
                        </Route>
                        <Route path="/veislur" exact>
                            <Redirect to="/pantamat?party=true" />
                        </Route>

                        <Route path="/harpa" exact>
                            <Redirect to="/harpaveitingasala/order" />
                        </Route>

                        <Route
                            path="/restaurant"
                            exact
                            component={Restaurant}
                        />
                        <Route path="/terms" exact component={Terms} />
                        <Route
                            path="/unsubscribe"
                            exact
                            component={Unsubscribe}
                        />
                        <Route
                            path="/styleguide"
                            exact
                            component={Styleguide}
                        />
                        <Route
                            path="/paymentlink"
                            exact
                            component={PaymentLink}
                        />
                        <Route
                            path="/additionalinfo"
                            exact
                            component={PaymentLink}
                        />
                        <Route
                            path="/cancelbooking"
                            exact
                            component={CancelBooking}
                        />

                        <Route path="/checkout" component={Checkout} />
                        <Route path="/book" component={BookingProcess} />
                        <Route path="/complete" exact component={Complete} />
                        <Route
                            path="/confirmation"
                            exact
                            component={Confirmation}
                        />

                        <Route path="/signup" component={EmailSignup} exact />
                        <Route
                            path="/signup/thankyou"
                            component={ThankYou}
                            exact
                        />

                        <Route path="/login" component={LoginPage} exact />
                        <Route path="/logout" component={Logout} exact />

                        <Route path="/jol" component={Xmas} exact />
                        <Route path="/jolahladbord" component={Xmas} exact />
                        <Route path="/christmas" component={Xmas} exact />
                        <Route path="/xmas" component={Xmas} exact />
                        <Route path="/vidburdir" component={Xmas} exact />

                        <Route
                            path="/giftcard/status"
                            component={GiftCardStatus}
                            exact
                        />
                        <Route
                            path="/giftcard/view/:code"
                            component={GiftCardView}
                            exact
                        />

                        <Route
                            path="/nearby/:lat/:lon/:take/:isHarpa?/:language"
                            component={Nearby}
                        />

                        <Route
                            path="/survey"
                            component={SatisfactionSurvey}
                            exact
                        />
                        <Route
                            path="/survey/thankyou"
                            component={ThankYou}
                            exact
                        />

                        <Route
                            path="/:restaurantSlug/order"
                            component={TALayout}
                        />

                        <Route
                            path="/:restaurantSlug/guestlist"
                            component={GuestList}
                        />

                        <Route
                            path="/:restaurantSlug/event/:eventSlug"
                            component={EventRedirect}
                            exact
                        />
                        <Route path="/:restaurantSlug" component={Restaurant} />
                    </Switch>
                </MuiThemeProvider>
            </ErrorBoundary>
            {process.env.NODE_ENV === 'development' && (
                <ReactQueryDevtools initialIsOpen={false} />
            )}
        </QueryClientProvider>
    );
};

export default withRouter(Layout);
